import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastService } from '../services/toast.service';
import { ApiErrorMessages } from '../utils/api-error-messages';
import { isRequestCancellation } from '../utils/api-helpers';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslocoService);
  // Upload failures are explained by the page itself (CesCampaignAuscultationComponent.onSave).
  private readonly silentEndpoints = ['/websocket/address', '/files', '/file-uploads'];

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: unknown) => {
        // 401/403 belong to AuthRefreshInterceptor's recovery flow (silent
        // refresh, then a redirect through Entra). Surfacing a toast here would race the
        // redirect and confuse the user.
        if (
          error instanceof HttpErrorResponse &&
          error.status !== 401 &&
          error.status !== 403 &&
          !this.isSilentEndpoint(request.url)
        ) {
          this.showErrorToast(error);
        }
        return throwError(() => error);
      }),
    );
  }

  private showErrorToast(error: HttpErrorResponse): void {
    if (isRequestCancellation(error)) return;

    const body: unknown = error.error;
    const hasMessage = (v: unknown): v is { message: string } =>
      typeof v === 'object' && v !== null && 'message' in v && typeof v.message === 'string';
    const errorCode = hasMessage(body) ? body.message : null;
    const translatedMessage = errorCode ? this.translateErrorCode(errorCode) : null;

    if (translatedMessage) {
      this.toastService.error(translatedMessage, true);
    }
  }

  private isSilentEndpoint(url: string): boolean {
    return this.silentEndpoints.some(endpoint => {
      const index = url.indexOf(endpoint);
      if (index === -1) return false;
      const afterEndpoint = url[index + endpoint.length];
      return !afterEndpoint || afterEndpoint === '?';
    });
  }

  private translateErrorCode(code: string): string | null {
    if (!code) return null;
    const lang = this.translate.getActiveLang();
    return ApiErrorMessages[lang]?.[code] ?? ApiErrorMessages['en']?.[code] ?? null;
  }
}
